import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Head from '../layouts/Head';
import LoadingDashboardContent from '../../components/LoadingDashboardContent';
import Pagination from '../../components/Pagination';
import TransactionPdf from './TransactionPdf';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { formatCurrency, formatDateTime } from '../../utils/appUtils';
import { API_URL } from '../../constants';
import { DateRangePicker } from 'rsuite';
import 'rsuite/DateRangePicker/styles/index.css';

const API_BASE = `${API_URL}/transactions`;

const AdminTransactions = ({ userRole, userToken }) => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(search);
  const [vendorId, setVendorId] = useState(queryParams.get('vendorId') || '');
  const [orderId, setOrderId] = useState(queryParams.get('orderId') || '');
  const [dateRange, setDateRange] = useState([]);
  const [limit, setLimit] = useState(Number(queryParams.get('limit')) || 20);
  const [page, setPage] = useState(Number(queryParams.get('page')) || 1);

  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [totalStats, setTotalStats] = useState({});
  const [dataCount, setDataCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const formatDateToSQL = (date) => {
    const pad = (n) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const buildParams = () => {
    const params = {};
    if (vendorId) params.vendorId = vendorId;
    if (orderId) params.orderId = orderId;
    if (dateRange?.length === 2) {
      params.dateFrom = formatDateToSQL(dateRange[0]);
      params.dateTo = formatDateToSQL(dateRange[1]);
    }
    params.limit = limit;
    params.page = page;
    return params;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE, {
        params: buildParams(),
        headers: { Authorization: `Bearer ${userToken}` },
      });
      const { data, total, dataCount, totalPages } = res.data;
      setTransactions(data);
      setTotalStats(total);
      setDataCount(dataCount);
      setTotalPages(totalPages);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const updateURL = () => {
    const params = buildParams();
    const query = new URLSearchParams(params).toString();
    navigate({ search: `?${query}` });
  };

  useEffect(() => {
    fetchData();
  }, [search]);

  const handleSearch = () => {
    setPage(1);
    updateURL();
  };

  const handleChange = (key, value) => {
    if (key === 'page') setPage(value);
    if (key === 'limit') setLimit(value);
  };

  useEffect(() => {
    updateURL();
  }, [page, limit]);

  return (
    <>
      <Head title="Vendor Transactions" />
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
        <h1 className="fs-4 m-0 lh-base">Transactions</h1>
      </div>

      <div className="row g-2 mb-3 align-items-end">
        <div className="col-auto">
          <label className="form-label">Vendor ID</label>
          <input
            type="text"
            className="form-control"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label className="form-label">Order ID</label>
          <input
            type="text"
            className="form-control"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label className="form-label">Date Range</label>
          <DateRangePicker value={dateRange} onChange={setDateRange} style={{ width: 220 }} />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" onClick={handleSearch}>Search</button>
        </div>
        <div className="col-auto">
          <PDFDownloadLink
            document={<TransactionPdf transactions={transactions} summary={totalStats} />}
            fileName="transactions.pdf"
            className="btn btn-outline-secondary"
          >
            Download PDF
          </PDFDownloadLink>
        </div>
      </div>

      {loading ? (
        <LoadingDashboardContent />
      ) : (
        <>
          <div className="row g-2 mb-3">
            <div className="col bg-light p-3 border">Order Total: <strong>{formatCurrency(totalStats.totalOrderAmount)}</strong></div>
            <div className="col bg-light p-3 border">Vendor Get: <strong>{formatCurrency(totalStats.totalVendorEarning)}</strong></div>
            <div className="col bg-light p-3 border">Platform Charge: <strong>{formatCurrency(totalStats.totalPlatformCommission)}</strong></div>
            <div className="col bg-light p-3 border">Tax: <strong>{formatCurrency(totalStats.totalTaxAmount)}</strong></div>
            <div className="col bg-light p-3 border">Delivery: <strong>{formatCurrency(totalStats.totalDeliveryCharge)}</strong></div>
          </div>

          <div className="admin-table table-responsive small border-top border-4">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Vendor</th>
                  <th>Order Total</th>
                  <th>Vendor Get</th>
                  <th>Platform Charge</th>
                  <th>Tax</th>
                  <th>Delivery</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id}>
                    <td>{t.orderId}</td>
                    <td>{t.vendor_name} <span className="badge bg-secondary">#{t.vendor_id}</span></td>
                    <td>{formatCurrency(t.orderAmount)}</td>
                    <td>{formatCurrency(t.vendorEarning)}</td>
                    <td>{formatCurrency(t.platformCommission)}</td>
                    <td>{formatCurrency(t.taxAmount)}</td>
                    <td>{formatCurrency(t.deliveryCharge)}</td>
                    <td>{formatDateTime(t.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="form-text">
              Total {dataCount} records found, page {page} of {totalPages}
            </div>
            <div className="d-flex align-items-center">
              <label className="me-2">Limit</label>
              <select
                className="form-select form-select-sm"
                style={{ width: 80 }}
                value={limit}
                onChange={(e) => handleChange('limit', parseInt(e.target.value))}
              >
                {[10, 20, 50, 100].map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
          </div>

          <Pagination totalPages={totalPages} currentPage={page} handleChange={handleChange} />
        </>
      )}
    </>
  );
};

export default AdminTransactions;